/**
 * Order stats index for segment predicate evaluators (pure).
 */

import { getOrderDocumentTimeMs } from '../utils/orderDocumentTime';
import { isoWeekKey, windowStartMsForDays } from './segmentEvalTimestamps';

const clean = (v) => (v == null ? '' : String(v).trim());

/**
 * @typedef {object} OrderUserStats
 * @property {number} lifetimeCount
 * @property {number|null} lastOrderMs
 * @property {number[]} orderTimesMs
 */

/**
 * @typedef {object} OrderStatsIndex
 * @property {Map<string, OrderUserStats>} byUid
 * @property {Map<string, OrderUserStats>} byEmail
 */

/**
 * @returns {OrderUserStats}
 */
export function emptyOrderUserStats() {
  return { lifetimeCount: 0, lastOrderMs: null, orderTimesMs: [] };
}

/**
 * @param {object[]} orders
 * @returns {OrderStatsIndex}
 */
export function buildOrderStatsIndex(orders) {
  /** @type {Map<string, OrderUserStats>} */
  const byUid = new Map();
  /** @type {Map<string, OrderUserStats>} */
  const byEmail = new Map();

  const bump = (map, key, ms) => {
    if (!key) return;
    let stat = map.get(key);
    if (!stat) stat = emptyOrderUserStats();
    stat.lifetimeCount += 1;
    stat.orderTimesMs.push(ms);
    if (stat.lastOrderMs == null || ms > stat.lastOrderMs) stat.lastOrderMs = ms;
    map.set(key, stat);
  };

  for (const order of orders || []) {
    const ms = getOrderDocumentTimeMs(order);
    if (ms == null) continue;
    const uid = clean(order.DOCMatch_id || order.customer?.DOCMatch_id);
    const email = clean(order.email || order.customer?.email).toLowerCase();
    bump(byUid, uid, ms);
    bump(byEmail, email, ms);
  }

  return { byUid, byEmail };
}

/**
 * @param {object} user
 * @param {OrderStatsIndex} index
 * @returns {OrderUserStats}
 */
export function orderStatsForUser(user, index) {
  const uid = clean(user?.id);
  const email = clean(user?.email || user?.Email).toLowerCase();
  return (
    (uid && index.byUid.get(uid)) ||
    (email && index.byEmail.get(email)) ||
    emptyOrderUserStats()
  );
}

/**
 * @param {OrderUserStats} stat
 * @param {number} withinDays
 * @param {number} nowMs
 */
export function countOrdersInWindow(stat, withinDays, nowMs) {
  const start = windowStartMsForDays(withinDays, nowMs);
  return (stat.orderTimesMs || []).filter((ms) => ms >= start && ms <= nowMs).length;
}

/**
 * @param {OrderUserStats} stat
 * @param {number} lookbackWeeks
 * @param {number} nowMs
 * @returns {Set<string>}
 */
export function distinctIsoWeeksInLookback(stat, lookbackWeeks, nowMs) {
  const start = nowMs - Math.max(1, lookbackWeeks) * 7 * 24 * 60 * 60 * 1000;
  const weeks = new Set();
  for (const ms of stat.orderTimesMs || []) {
    if (ms >= start && ms <= nowMs) weeks.add(isoWeekKey(ms));
  }
  return weeks;
}

/**
 * @param {OrderUserStats} stat
 * @param {number} withinDays
 * @param {number} nowMs
 */
export function hasOrderWithinDays(stat, withinDays, nowMs) {
  return countOrdersInWindow(stat, withinDays, nowMs) >= 1;
}
